import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Marker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import RGBcolor from 'color';
import { CompetitorType, TrackPoint, useEventContext } from 'providers/EventProvider';
import { useSplitContext } from 'providers/SplitProvider';

const ARROW_SIZE = 22;
const CIRCLE_SIZE = 14;
const ARROW_NAME_GAP_PX = 12;
const CIRCLE_NAME_GAP_PX = 12;
const HIT_ANIMATION_MS = 2000;
const HIT_CIRCLE_SIZE = 80;

const getBearing = (from: TrackPoint, to: TrackPoint) => {
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const cssFromStyle = (style: Record<string, string>) =>
  Object.entries(style)
    .map(([key, value]) => `${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}:${value}`)
    .join(';');

const createHeadIcon = (
  size: number,
  nameGap: number,
  shapeHtml: string,
  shortName: string,
  nameStyle: Record<string, string>,
  highlight: boolean
) =>
  L.divIcon({
    className: 'player-head-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div class="player-head-marker">
      ${shapeHtml}
      <span class="player-head-name${highlight ? ' highlight_on' : ''}" style="left:calc(50% + ${nameGap}px);${cssFromStyle(nameStyle)}">${escapeHtml(shortName)}</span>
    </div>`
  });

const createArrowIcon = (
  fillColor: string,
  strokeColor: string,
  bearing: number,
  shortName: string,
  nameStyle: Record<string, string>,
  highlight: boolean
) =>
  createHeadIcon(
    ARROW_SIZE,
    ARROW_NAME_GAP_PX,
    `<svg width="${ARROW_SIZE}" height="${ARROW_SIZE}" viewBox="0 0 24 24" style="transform:rotate(${bearing}deg)">
      <path d="M12 2 L20 18 L12 14.4 L4 18 Z" fill="${fillColor}" stroke="${strokeColor}" stroke-width="1.75" stroke-linejoin="round"/>
    </svg>`,
    shortName,
    nameStyle,
    highlight
  );

const createCircleIcon = (fillColor: string, shortName: string, nameStyle: Record<string, string>, highlight: boolean) =>
  createHeadIcon(
    CIRCLE_SIZE,
    CIRCLE_NAME_GAP_PX,
    `<svg width="${CIRCLE_SIZE}" height="${CIRCLE_SIZE}" viewBox="0 0 14 14">
      <circle cx="7" cy="7" r="5" fill="${fillColor}" stroke="black" stroke-width="2"/>
    </svg>`,
    shortName,
    nameStyle,
    highlight
  );

const createHitCircleIcon = (fillColor: string) =>
  L.divIcon({
    className: 'player-hit-icon',
    iconSize: [HIT_CIRCLE_SIZE, HIT_CIRCLE_SIZE],
    iconAnchor: [HIT_CIRCLE_SIZE / 2, HIT_CIRCLE_SIZE / 2],
    html: `<div class="player-hit-circle" style="background:${fillColor}"></div>`
  });

const getPositionAtElapsed = (track: TrackPoint[], elapsedMs: number): TrackPoint | undefined => {
  if (track.length === 0) return undefined;
  const startTime = track[0].time;
  const nextTime = startTime + elapsedMs;
  let last: TrackPoint | undefined;
  let next: TrackPoint | undefined;
  for (const point of track) {
    if (point.time <= nextTime) {
      last = point;
    } else {
      next = point;
      break;
    }
  }
  if (!last) return track[0];
  if (!next || next.time === last.time) return last;
  const k = (nextTime - last.time) / (next.time - last.time);
  return {
    lat: last.lat + (next.lat - last.lat) * k,
    lng: last.lng + (next.lng - last.lng) * k,
    time: last.time + k
  };
};

const calculateStyle = (player: CompetitorType) => {
  const rgb = new RGBcolor(player.display.color);
  const shadowColor = rgb.red() * 0.299 + rgb.green() * 0.587 + rgb.blue() * 0.114 > 186 ? '#000000' : '#ffffff';
  return {
    color: player.display.color,
    textShadow: `-1px -1px 0 ${shadowColor}, 1px -1px 0 ${shadowColor}, -1px 1px 0 ${shadowColor}, 1px 1px 0 ${shadowColor}`,
    fontWeight: 'bold'
  };
};

const MarkerPlayer = (props: MarkerPlayerProps) => {
  const { event } = useEventContext();
  const {
    time,
    options,
    mapBearing,
    activeSplits,
    followedPlayerId,
    controls,
    setMapCenter,
    setMapBearing,
    setMapTransitionMs,
    setMapFitPoints
  } = useSplitContext();
  const { player } = props;
  const markerOpacity = options.opacity / 100;
  const [polyLine, setPolyLine] = useState<TrackPoint[]>([]);
  const [marker, setMarker] = useState<TrackPoint>();
  const [style, setStyle] = useState(calculateStyle(player));
  const [hitKey, setHitKey] = useState(0);
  const [showHit, setShowHit] = useState(false);
  const [hitPosition, setHitPosition] = useState<TrackPoint>();
  const prevTimeRef = useRef(time);

  const punchTimesMs = useMemo(() => {
    let elapsedMs = 0;
    const times: number[] = [];
    for (const splitIndex of activeSplits) {
      elapsedMs += (player.splits[splitIndex]?.time ?? 0) * 1000;
      times.push(elapsedMs);
    }
    return times;
  }, [activeSplits, player.splits]);

  const bearing = useMemo(() => {
    if (polyLine.length < 2) return 0;
    const last = polyLine[polyLine.length - 1];
    let fromIdx = polyLine.length - 2;
    while (fromIdx >= 0 && polyLine[fromIdx].lat === last.lat && polyLine[fromIdx].lng === last.lng) {
      fromIdx--;
    }
    if (fromIdx < 0) return 0;
    return (getBearing(polyLine[fromIdx], last) + mapBearing + 360) % 360;
  }, [polyLine, mapBearing]);

  const playerIcon = useMemo(() => {
    const fillColor = player.display.color;
    const { zIndex: _zIndex, ...nameStyle } = style as Record<string, string>;
    switch (event.markerType) {
      case 'circle':
        return createCircleIcon(fillColor, player.shortName, nameStyle, player.options.highlight);
      case 'arrow': {
        const strokeColor = new RGBcolor(fillColor).mix(new RGBcolor('#000000'), 0.5).hex();
        return createArrowIcon(fillColor, strokeColor, bearing, player.shortName, nameStyle, player.options.highlight);
      }
      default: {
        const _exhaustive: never = event.markerType;
        throw new Error(`Unhandled marker type: ${_exhaustive}`);
      }
    }
  }, [event.markerType, player.display.color, player.shortName, player.options.highlight, bearing, style]);

  const hitIcon = useMemo(() => createHitCircleIcon(player.display.color), [player.display.color]);

  useEffect(() => {
    const prevTime = prevTimeRef.current;
    prevTimeRef.current = time;

    if (time === 0) {
      setShowHit(false);
      setHitPosition(undefined);
      return;
    }

    if (time < prevTime) return;

    let lastPunch: number | null = null;
    let lastPunchIndex = -1;
    for (let i = 0; i < punchTimesMs.length; i++) {
      const punchTime = punchTimesMs[i];
      if (prevTime < punchTime && time >= punchTime) {
        lastPunch = punchTime;
        lastPunchIndex = i;
      }
    }
    if (lastPunch !== null) {
      const position = getPositionAtElapsed(player.display.track, lastPunch);
      if (position) {
        setHitPosition(position);
        setHitKey((key) => key + 1);
        setShowHit(true);
      }
      if (followedPlayerId === player.id) {
        const nextSplitIndex = activeSplits[lastPunchIndex + 1];
        if (nextSplitIndex != null) {
          const startControl = controls[nextSplitIndex];
          const nextControl = controls[nextSplitIndex + 1];
          if (startControl && nextControl) {
            setMapTransitionMs(HIT_ANIMATION_MS);
            setMapCenter({
              lat: (startControl.position.lat + nextControl.position.lat) / 2,
              lng: (startControl.position.lng + nextControl.position.lng) / 2
            });
            const heading = getBearing(startControl.position, nextControl.position);
            setMapBearing((360 - heading) % 360);
            setMapFitPoints([
              { lat: startControl.position.lat, lng: startControl.position.lng },
              { lat: nextControl.position.lat, lng: nextControl.position.lng }
            ]);
          }
        }
      }
    }
  }, [
    time,
    punchTimesMs,
    player.display.track,
    player.id,
    followedPlayerId,
    activeSplits,
    controls,
    setMapCenter,
    setMapBearing,
    setMapTransitionMs,
    setMapFitPoints
  ]);

  useEffect(() => {
    if (!showHit) return;
    const timeout = window.setTimeout(() => setShowHit(false), HIT_ANIMATION_MS);
    return () => window.clearTimeout(timeout);
  }, [showHit, hitKey]);

  useEffect(() => {
    const polyLine: TrackPoint[] = [];

    if (player.display.track.length > 0) {
      const startTime = player.display.track[0].time;
      const nextTime = startTime + time;
      player.display.track.forEach((d) => {
        if (time === 0 || d.time <= startTime + time) {
          polyLine.push(d);
        }
      });

      if (polyLine.length > 0) {
        const last = polyLine[polyLine.length - 1];
        const next = player.display.track.filter((d) => d.time > last.time);
        if (next && next[0]) {
          const start = last;
          const second = next[0];
          const k = (nextTime - start.time) / (second.time - start.time);
          const vLat = (second.lat - start.lat) * k;
          const vLng = (second.lng - start.lng) * k;
          const end = {
            lat: start.lat + vLat,
            lng: start.lng + vLng,
            time: start.time + k
          };
          polyLine.push(end);
        }
      } else {
        const last = player.display.track.filter((d) => d.time < nextTime);
        if (last && last[last.length - 1]) {
          polyLine.push(last[last.length - 1]);
        }
      }
    }

    setPolyLine(polyLine);
    if (polyLine.length) {
      const newMarker = polyLine[polyLine.length - 1];
      setMarker(newMarker);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time, player.display.track]);

  useEffect(() => {
    const newStyle = calculateStyle(player);
    if (player.options.highlight) {
      Object.assign(newStyle, {
        padding: '6px',
        background: 'white',
        border: '1px solid black',
        borderRadius: '5px',
        marginLeft: '-5px',
        zIndex: 'inherit'
      });
    }
    setStyle(newStyle);
  }, [player]);

  if (polyLine.length === 0) return null;

  return (
    <React.Fragment>
      <Polyline
        positions={polyLine}
        pathOptions={{
          color: player.display.color,
          weight: options.weight,
          opacity: options.opacity / 100
        }}
      />
      {showHit && hitPosition && (
        <Marker key={hitKey} position={hitPosition} icon={hitIcon} interactive={false} zIndexOffset={900} />
      )}
      {time > 0 && marker && (
        <Marker
          position={marker}
          icon={playerIcon}
          opacity={markerOpacity}
          zIndexOffset={player.options.highlight ? 1000 : 0}
        />
      )}
    </React.Fragment>
  );
};

export default MarkerPlayer;

type MarkerPlayerProps = {
  player: CompetitorType;
};
